import React, { useState, useRef } from "react";
import {
  Box,
  TextField,
  Paper,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Button,
  Typography,
  ClickAwayListener,
  CircularProgress,
  ListItemButton,
  Divider,
  useTheme,
  alpha,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import EmailIcon from "@mui/icons-material/Email";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import SearchOffIcon from "@mui/icons-material/SearchOff";
import { ImageAvatar } from "components/Circle/shared/ImageAvatar";
import { useDebouncedValue } from "hooks/useDebouncedValue";
import { useUserSearchQuery } from "components/Slices/circleApiSlice";

// eslint-disable-next-line react/prop-types
const AddNewUsers = ({ potentialMembers, handleUserSelect, errors }) => {
  const theme = useTheme();
  const [query, setQuery] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);

  const debouncedQuery = useDebouncedValue(query, 300);
  const { data: results, isLoading } = useUserSearchQuery({
    email: debouncedQuery,
  });



  const handleInvite = () => {
    const email = query.trim().toLowerCase();
    if (!email) return;
    // Add as a to-invite entry (no user id yet). createCircle creates a placeholder
    // for this email and the person claims it when they sign up with it.
    handleUserSelect({
      id: "",
      email,
      firstName: email.split("@")[0],
      lastName: "",
      displayImg: null,
    });
    setQuery("");
    setIsOpen(false);
  };

  //filter results from query and exclude members already in the group and admine
  const filteredResults =
    results?.users?.filter(
      (user) =>
        !potentialMembers.some(
          (member) => member.id === user.id || member.email === user.email
        ) && user.email.includes(query.trim())
    ) || [];

  return (
    <ClickAwayListener
      onClickAway={() => {
        setIsOpen(false);
      }}
    >
      <Box ref={containerRef} sx={{ position: "relative", width: "100%" }}>
        <TextField
          fullWidth
          variant="outlined"
          type="search"
          placeholder="Search by email"
          value={query}
          autoComplete="off"
          error={!!errors.members}
          helperText={errors.members?.message}
          onChange={(e) => setQuery(e.target.value.trim())}
          onFocus={() => setIsOpen(true)}
          InputProps={{
            autoCorrect: "new-password",
            startAdornment: (
              <SearchIcon sx={{ color: "text.secondary", mr: 1, fontSize: "1.1rem" }} />
            ),
            endAdornment: isLoading && <CircularProgress size={16} sx={{ color: "text.disabled" }} />,
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: 2,
              fontSize: "0.88rem",
            },
          }}
        />
        {isOpen && (isLoading || query.trim()) && (
          <Paper
            elevation={0}
            sx={{
              position: "absolute",
              left: 0,
              right: 0,
              top: "calc(100% + 6px)",
              maxHeight: 260,
              overflow: "auto",
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 3,
              boxShadow: "0 4px 20px rgba(16,24,64,0.12), 0 1px 4px rgba(16,24,64,0.08)",
              zIndex: 1300,
            }}
          >
            {isLoading ? (
              <Box sx={{ px: 2.5, py: 2, display: "flex", alignItems: "center", gap: 1.5 }}>
                <CircularProgress size={16} sx={{ color: "primary.main" }} />
                <Typography sx={{ fontSize: "0.82rem", color: "text.secondary" }}>
                  Searching…
                </Typography>
              </Box>
            ) : filteredResults?.length > 0 ? (
              <List disablePadding>
                {filteredResults.map((user, index) => (
                  <Box key={user.id}>
                    <ListItemButton
                      onClick={() => handleUserSelect(user)}
                      sx={{
                        px: 2,
                        py: 1.25,
                        gap: 0,
                        "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.04) },
                      }}
                    >
                      <ListItemAvatar sx={{ minWidth: 46 }}>
                        <ImageAvatar
                          displayImg={user.displayImg ?? undefined}
                          firstName={user.firstName}
                          sx={{ width: 34, height: 34, fontSize: "0.8rem" }}
                        />
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Typography sx={{ fontSize: "0.88rem", fontWeight: 600, color: "text.primary", lineHeight: 1.3 }}>
                            {user.firstName} {user.lastName}
                          </Typography>
                        }
                        secondary={
                          <Typography sx={{ fontSize: "0.75rem", color: "text.secondary", mt: 0.1 }}>
                            {user.email}
                          </Typography>
                        }
                      />
                    </ListItemButton>
                    {index < filteredResults.length - 1 && <Divider sx={{ ml: 7.5 }} />}
                  </Box>
                ))}
              </List>
            ) : query.trim() && results?.canInvite ? (
              <Box sx={{ p: 2.5 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
                  <Box
                    sx={{
                      width: 34,
                      height: 34,
                      borderRadius: "50%",
                      bgcolor: alpha(theme.palette.primary.main, 0.08),
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <EmailIcon sx={{ fontSize: "1rem", color: "primary.main" }} />
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: "0.88rem", fontWeight: 600, color: "text.primary" }}>
                      No account found
                    </Typography>
                    <Typography sx={{ fontSize: "0.75rem", color: "text.secondary" }}>
                      Invite "{query}" by email
                    </Typography>
                  </Box>
                </Box>
                <Button
                  variant="contained"
                  startIcon={<PersonAddIcon sx={{ fontSize: "0.95rem !important" }} />}
                  onClick={handleInvite}
                  fullWidth
                  sx={{
                    borderRadius: "10px",
                    fontSize: "0.82rem",
                    fontWeight: 600,
                    py: 1,
                    textTransform: "none",
                  }}
                >
                  Send Invitation
                </Button>
              </Box>
            ) : query.trim() ? (
              <Box sx={{ px: 2.5, py: 2, display: "flex", alignItems: "center", gap: 1.5 }}>
                <SearchOffIcon sx={{ fontSize: "1.1rem", color: "text.disabled" }} />
                <Typography sx={{ fontSize: "0.82rem", color: "text.secondary" }}>
                  No results for "{query}"
                </Typography>
              </Box>
            ) : null}
          </Paper>
        )}
      </Box>
    </ClickAwayListener>
  );
};

export default AddNewUsers;
